
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { updateProfile } from '@/services/profileService';
import { AlertCircle, CheckCircle, Shield } from 'lucide-react';

export function AdminUserChecker() {
  const { profile, user } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState<string | null>(null);

  const makeCurrentUserAdmin = async () => {
    if (!user || !profile) return;
    
    setIsUpdating(true);
    try {
      await updateProfile(profile.id, { role: 'administrator' });
      setUpdateResult('success');
      // Refresh the page to reload user data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error('Error making user admin:', error);
      setUpdateResult('error');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!user) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          You need to be logged in to access the admin dashboard.
        </AlertDescription>
      </Alert>
    );
  }

  if (!profile) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Loading user profile...
        </AlertDescription>
      </Alert>
    );
  }

  const isAdmin = profile.role === 'administrator' || profile.role === 'admin';

  return (
    <Card className="max-w-md mx-auto">
      <CardHeader style={{ backgroundColor: 'orange' }}>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Admin Access Check
        </CardTitle>
        <CardDescription>
          Current user: {user.email}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isAdmin ? (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              ✅ You have administrator access (Role: {profile.role})
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                ❌ You don't have administrator access (Current role: {profile.role})
              </AlertDescription>
            </Alert>
            
            <Button 
              onClick={makeCurrentUserAdmin}
              disabled={isUpdating}
              className="w-full"
            >
              {isUpdating ? 'Updating...' : 'Make Me Administrator'}
            </Button>
          </div>
        )}
        
        {updateResult === 'success' && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              Successfully updated your role to administrator! Refreshing page...
            </AlertDescription>
          </Alert>
        )}
        
        {updateResult === 'error' && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to update role. Please try again or check console for errors.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
